import { yupResolver } from "@hookform/resolvers/yup";
import { ButtonSet, Form, Loading } from "carbon-components-react";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import CancelButton from "../../components/Buttons/CancelButton";
import SubmitButton from "../../components/Buttons/SubmitButton";
import Card from "../../components/Card/Card";
import { basicBusinessPlanSchema } from "../../services/SchemaService";
import useAuth from "../../hooks/useAuth";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import http from "../../http-common";
import {
  transformCheckboxArraysToBooleans,
  transformDropdownValues,
} from "../../services/Utils";
import { Tabs } from "carbon-components-react";
import { Tab } from "carbon-components-react";
import { Button } from "carbon-components-react";
import { ArrowRight20 } from "@carbon/icons-react";
import { ArrowLeft20 } from "@carbon/icons-react";
import BusinessPlanFillingInstructions from "../../components/Miscellaneous/BusinessPlanFillingInstructions";
import EnvironmentalSocialAnalysisForm from "../../components/Forms/EnvironmentalSocialAnalysisForm";
import FinancialPlanForm from "../../components/Forms/FinancialPlanForm";
import MarketAnalysisForm from "../../components/Forms/MarketAnalysisForm";
import ExistingBusinessProfileForm from "../../components/Forms/ExistingBusinessProfileForm";
import DataSheetForm from "../../components/Forms/DataSheetForm";
import WorkersForm from "../../components/Forms/WorkersForm";

const CreateBusinessPlanPage = () => {
  const [competitor, setCompetitor] = useState(null);
  const [isQualified, setIsQualified] = useState(false);
  const [hasBusinessCurrently, setHasBusinessCurrently] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isEligible, setIsEligible] = useState(false);
  const [businessPlan, setBusinessPlan] = useState(null);
  const { user, isCompetitor } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // The generic "Planos de Negócio > Registar" menu entry links here with no
  // ?window= param at all - only the dashboard's per-window buttons pass one
  // explicitly. When absent, the actual window is only known once the
  // competitor's eligibility has loaded (see initializeBusinessPlan below),
  // so this starts as a best-effort guess and gets corrected in the effect.
  const explicitWindowParam = searchParams.get("window");
  const [windowCode, setWindowCode] = useState(
    explicitWindowParam ? Number(explicitWindowParam) : 1
  );
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(basicBusinessPlanSchema),
    mode: "onBlur",
  });

  const onSubmit = async (data) => {
    try {
      // Transform checkbox arrays to boolean values and dropdown values before sending to backend
      let transformedData = transformCheckboxArraysToBooleans(data);
      transformedData = transformDropdownValues(transformedData);
      const response = await http.post("/business-plan", transformedData);
      toast.success("Plano de Negócios Registado com sucesso");
      navigate("../plano-de-negocio/" + response.data.uuid);
    } catch (error) {
      toast.error(error?.response?.data.message);
    }
  };

  useEffect(() => {
    const initializeBusinessPlan = async () => {
      try {
        setIsLoading(true);

        // Step 1: Check competitor eligibility (a candidate only ever has
        // one candidature - Janela 2 only unlocks a second business plan)
        const competitorResponse = await http.get(
          `/business-plan/competitor/${user.username}`
        );

        if (!competitorResponse.data) {
          toast.error("Candidato não encontrado");
          navigate("../inicio");
          return;
        }

        const competitorData = competitorResponse.data;
        setCompetitor(competitorData);

        // Step 2: Resolve which window this page is actually about. Explicit
        // ?window= (from the dashboard's per-window buttons) always wins;
        // otherwise (generic "Planos de Negócio > Registar" menu entry) prefer
        // Janela 2 when the candidate is eligible for it, since that's the
        // window still open for submission once Janela 1 is done.
        const resolvedWindow = explicitWindowParam
          ? Number(explicitWindowParam)
          : competitorData.eligibleForWindow2
          ? 2
          : 1;
        setWindowCode(resolvedWindow);
        const resolvedNurPrefix = resolvedWindow === 2 ? "NU2" : "NUR";

        const eligibleForThisWindow =
          resolvedWindow === 2 ? competitorData.eligibleForWindow2 : competitorData.isEligible;
        if (!eligibleForThisWindow) {
          toast.error(
            "Somente candidatos apurados podem registar o plano de negócios"
          );
          setIsEligible(false);
          setIsLoading(false);
          return;
        }

        setIsQualified(true);
        setIsEligible(true);
        setValue("nur", `${resolvedNurPrefix}${user.username}`);

        // Step 3: Check if business plan already exists
        try {
          const businessPlanResponse = await http.get(
            `/business-plan/nur/${resolvedNurPrefix}${user.username}`
          );

          if (businessPlanResponse.data) {
            const existingBusinessPlan = businessPlanResponse.data;
            setBusinessPlan(existingBusinessPlan);

            // Step 4: Route based on business plan status
            if (existingBusinessPlan.currentStatusCode === 1) {
              // Status 1: Draft - redirect to edit
              toast.info(
                "Plano de negócios existente encontrado. Redirecionando para edição..."
              );
              navigate(
                "../editar-plano-de-negocio/" + existingBusinessPlan.uuid
              );
              return;
            } else {
              // Status > 1: Submitted - redirect to view
              toast.info(
                "Plano de negócios já submetido. Redirecionando para visualização..."
              );
              navigate("../plano-de-negocio/" + existingBusinessPlan.uuid);
              return;
            }
          }
        } catch (businessPlanError) {
          // Business plan doesn't exist - continue with creation
          console.log(
            "No existing business plan found, proceeding with creation"
          );
        }

        // Step 5: If we reach here, competitor is eligible and no business plan exists
        // Show the creation form
        setIsLoading(false);
      } catch (error) {
        console.error("Error initializing business plan:", error);
        toast.error(error?.response?.data?.message || "Erro ao carregar dados");
        setIsLoading(false);
      }
    };

    if (user?.username) {
      initializeBusinessPlan();
    }
  }, [user, navigate, setValue, explicitWindowParam]);

  const nextTab = () => {
    setActiveTab((prev) => prev + 1);
  };
  const previousTab = () => {
    setActiveTab((prev) => prev - 1);
  };

  // Show loading state while checking eligibility and business plan status
  if (isLoading) {
    return (
      <Card title="Registar Plano de Negócio">
        <Loading
          active={isLoading}
          description="Verificando elegibilidade e carregando dados..."
          withOverlay={true}
        />
      </Card>
    );
  }

  // Show error message if competitor is not eligible
  if (!isEligible) {
    return (
      <Card title="Registar Plano de Negócio">
        <div style={{ padding: "2rem" }}>
          <h4>Plano de Negócios</h4>
          <br />
          <h6>Caro/a Empreendedor/a</h6>
          <br />
          <p>
            Agradecemos por tentar aceder ao módulo de submissão dos planos de
            negócio no âmbito do Conecta Negócios, na componente de Ligações de
            Consumo.
          </p>

          <p>
            Informamos que são elegíveis para submeter os planos de negócios na{" "}
            <strong>
              presente Janela de submissão (Janela 1) os/as empreendedores/as
              que até ao dia 23 de Outubro de 2025 concluíram as seguintes
              formações:
            </strong>
          </p>

          <ol>
            <li>
              <strong>Iniciativa Pessoal</strong>;
            </li>
            <li>
              <strong>Gestão Operacional</strong>;
            </li>
          </ol>

          <p className="mt-2">
            Serão elegíveis para submeter planos de negócios na Janela 2 os/as
            empreendedores/as que até ao dia{" "}
            <strong>23 de Outubro de 2025</strong> tenham concluído a formação
            em <strong>Iniciativa Pessoal</strong>, porém ainda não concluíram a
            formação em <strong>Gestão Operacional</strong>. Caso este seja o
            seu,{" "}
            <strong>
              por favor, certifique-se de ao ser convocado/a a participar da
              formação em Gestão Operacional, participe e conclua a mesma
            </strong>{" "}
            por forma a ser elegível para a Janela 2.
          </p>
          <br />
          <p>
            <strong>
              Caso não tenha concluído nenhuma das duas formações, lamentamos
              informar que não poderá ser elegível a participar das janelas de
              submissão de planos de negócio de acordo com as regras desta
              competição.
            </strong>{" "}
            No entanto, <strong>encorajamos a não desanimar</strong> e
            participar de forma activa nas futuras oportunidades.
          </p>
          <br />
          <Button onClick={() => navigate("../inicio")}>
            Voltar ao Início
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card title="Registar Plano de Negócio">
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Tabs
          scrollIntoView={false}
          selected={activeTab}
          type={"container"}
          onSelectionChange={(e) => setActiveTab(e)}
        >
          <Tab href="#" id="tab-0" label="INSTRUÇÕES">
            <BusinessPlanFillingInstructions />
            <div className="tab-navigation">
              <p></p>
              <Button kind="ghost" size="sm" onClick={nextTab}>
                Próximo <ArrowRight20 />
              </Button>
            </div>
          </Tab>
          <Tab href="#" id="tab-1" label="SECÇÃO 1">
            <DataSheetForm
              register={register}
              control={control}
              errors={errors}
              setHasBusinessCurrently={setHasBusinessCurrently}
              setValue={setValue}
              getValues={getValues}
              windowCode={windowCode}
            />
            <div className="tab-navigation">
              <Button kind="ghost" size="sm" onClick={previousTab}>
                <ArrowLeft20 /> Anterior
              </Button>
              <Button kind="ghost" size="sm" onClick={nextTab}>
                Próximo <ArrowRight20 />
              </Button>
            </div>
          </Tab>
          <Tab href="#" id="tab-2" label="SECÇÃO 2">
            <ExistingBusinessProfileForm
              register={register}
              control={control}
              errors={errors}
              setHasBusinessCurrently={setHasBusinessCurrently}
              hasBusinessCurrently={hasBusinessCurrently}
            />
            <div className="tab-navigation">
              <Button kind="ghost" size="sm" onClick={previousTab}>
                <ArrowLeft20 /> Anterior
              </Button>
              <Button kind="ghost" size="sm" onClick={nextTab}>
                Próximo <ArrowRight20 />
              </Button>
            </div>
          </Tab>
          <Tab href="#" id="tab-3" label="SECÇÃO 3">
            <MarketAnalysisForm register={register} errors={errors} />
            <div className="tab-navigation">
              <Button kind="ghost" size="sm" onClick={previousTab}>
                <ArrowLeft20 /> Anterior
              </Button>
              <Button kind="ghost" size="sm" onClick={nextTab}>
                Próximo <ArrowRight20 />
              </Button>
            </div>
          </Tab>
          <Tab href="#" id="tab-4" label="SECÇÃO 4">
            <WorkersForm
              register={register}
              errors={errors}
              control={control}
              getValues={getValues}
            />
            <div className="tab-navigation">
              <Button kind="ghost" size="sm" onClick={previousTab}>
                <ArrowLeft20 /> Anterior
              </Button>
              <Button kind="ghost" size="sm" onClick={nextTab}>
                Próximo <ArrowRight20 />
              </Button>
            </div>
          </Tab>
          <Tab href="#" id="tab-5" label="SECÇÃO 5">
            <FinancialPlanForm
              register={register}
              control={control}
              errors={errors}
              getValues={getValues}
              setValue={setValue}
            />
            <div className="tab-navigation">
              <Button kind="ghost" size="sm" onClick={previousTab}>
                <ArrowLeft20 /> Anterior
              </Button>
              <Button kind="ghost" size="sm" onClick={nextTab}>
                Próximo <ArrowRight20 />
              </Button>
            </div>
          </Tab>
          <Tab href="#" id="tab-6" label="SECÇÃO 6">
            <EnvironmentalSocialAnalysisForm
              register={register}
              errors={errors}
            />
            <div className="tab-navigation">
              <Button kind="ghost" size="sm" onClick={previousTab}>
                <ArrowLeft20 /> Anterior
              </Button>
            </div>
          </Tab>
        </Tabs>
        <ButtonSet>
          <SubmitButton />
          <CancelButton />
        </ButtonSet>
      </Form>
    </Card>
  );
};

export default CreateBusinessPlanPage;
